import { Type } from '@scalar/typebox';
import { compose } from '../../schemas/compose.js';
import { XScalarCredentialsLocationSchema } from '../../schemas/extensions/security/x-scalar-credentials-location.js';
import { XScalarAuthUrlSchema, XScalarSecretClientCertificateSchema, XScalarSecretClientIdSchema, XScalarSecretClientSecretSchema, XScalarSecretHTTPSchema, XScalarSecretPrivateKeySchema, XScalarSecretRedirectUriSchema, XScalarSecretRefreshTokenSchema, XScalarSecretServiceNameSchema, XScalarSecretTokenSchema, XScalarTokenUrlSchema, } from '../../schemas/extensions/security/x-scalar-security-secrets.js';
import { OAuthFlowAuthorizationCodeSchema, OAuthFlowClientCredentialsSchema, OAuthFlowDeviceAuthorizationSchema, OAuthFlowImplicitSchema, OAuthFlowPasswordSchema, } from '../../schemas/v3.2/strict/oauth-flow.js';
import { SecurityRequirementObjectSchema } from '../../schemas/v3.2/strict/openapi-document.js';
/** Clear intent is separate from legacy empty values that inherited configured credentials. */
const ClearedSecretFieldsSchema = Type.Object({
    'x-scalar-secret-cleared-fields': Type.Optional(Type.Array(Type.String())),
});
const SecretsApiKeySchema = compose(Type.Object({
    type: Type.Literal('apiKey'),
    /** User-entered parameter name, including an explicit empty value. */
    name: Type.Optional(Type.String()),
}), XScalarSecretTokenSchema, ClearedSecretFieldsSchema);
const SecretsHttpSchema = compose(Type.Object({
    type: Type.Literal('http'),
}), XScalarSecretTokenSchema, XScalarSecretHTTPSchema, ClearedSecretFieldsSchema);
const SecretsOAuthFlowCommonSchema = compose(XScalarAuthUrlSchema, XScalarTokenUrlSchema, ClearedSecretFieldsSchema, XScalarSecretClientIdSchema, XScalarSecretTokenSchema, XScalarSecretRefreshTokenSchema);
/** An absent stored redirect must fall back to configuration; an empty string means the user cleared it. */
const StoredRedirectUriSchema = Type.Partial(XScalarSecretRedirectUriSchema);
const SecretsOAuthFlowsSchema = Type.Object({
    deviceAuthorization: Type.Optional(compose(SecretsOAuthFlowCommonSchema, XScalarSecretClientSecretSchema, XScalarCredentialsLocationSchema)),
    implicit: Type.Optional(compose(SecretsOAuthFlowCommonSchema, StoredRedirectUriSchema)),
    password: Type.Optional(compose(SecretsOAuthFlowCommonSchema, XScalarSecretHTTPSchema, XScalarSecretClientSecretSchema, XScalarCredentialsLocationSchema)),
    clientCredentials: Type.Optional(compose(SecretsOAuthFlowCommonSchema, XScalarSecretClientSecretSchema, XScalarCredentialsLocationSchema)),
    authorizationCode: Type.Optional(compose(SecretsOAuthFlowCommonSchema, XScalarSecretClientSecretSchema, StoredRedirectUriSchema, XScalarCredentialsLocationSchema)),
});
const OAuthSchema = compose(Type.Object({
    type: Type.Literal('oauth2'),
}), SecretsOAuthFlowsSchema);
/** OpenID Connect schema contain the base flows as well since it doesn't exist in the spec */
export const OpenIDConnectSchema = Type.Object({
    deviceAuthorization: Type.Optional(compose(OAuthFlowDeviceAuthorizationSchema, SecretsOAuthFlowCommonSchema, XScalarSecretClientSecretSchema, XScalarCredentialsLocationSchema)),
    type: Type.Literal('openIdConnect'),
    implicit: Type.Optional(compose(OAuthFlowImplicitSchema, SecretsOAuthFlowCommonSchema, StoredRedirectUriSchema)),
    password: Type.Optional(compose(OAuthFlowPasswordSchema, SecretsOAuthFlowCommonSchema, XScalarSecretHTTPSchema, XScalarSecretClientSecretSchema, XScalarCredentialsLocationSchema)),
    clientCredentials: Type.Optional(compose(OAuthFlowClientCredentialsSchema, SecretsOAuthFlowCommonSchema, XScalarSecretClientSecretSchema, XScalarCredentialsLocationSchema)),
    authorizationCode: Type.Optional(compose(OAuthFlowAuthorizationCodeSchema, SecretsOAuthFlowCommonSchema, XScalarSecretClientSecretSchema, StoredRedirectUriSchema, XScalarCredentialsLocationSchema)),
});
/**
 * AsyncAPI SASL-style broker schemes: all of them authenticate with a username + password pair,
 * so they share one secrets shape and are matched to the scheme by the stored `type`.
 */
const SecretsSaslSchema = compose(Type.Object({
    type: Type.Union([
        Type.Literal('userPassword'),
        Type.Literal('plain'),
        Type.Literal('scramSha256'),
        Type.Literal('scramSha512'),
    ]),
}), XScalarSecretHTTPSchema, ClearedSecretFieldsSchema);
/** AsyncAPI X509 broker scheme: a client certificate + private key pair (PEM). */
const SecretsX509Schema = compose(Type.Object({
    type: Type.Literal('X509'),
}), XScalarSecretClientCertificateSchema, XScalarSecretPrivateKeySchema, ClearedSecretFieldsSchema);
/** AsyncAPI encryption broker schemes: a single key value, stored in the shared token slot. */
const SecretsEncryptionSchema = compose(Type.Object({
    type: Type.Union([Type.Literal('symmetricEncryption'), Type.Literal('asymmetricEncryption')]),
}), XScalarSecretTokenSchema, ClearedSecretFieldsSchema);
/** AsyncAPI GSSAPI (Kerberos) broker scheme: the service name the client authenticates against. */
const SecretsGssapiSchema = compose(Type.Object({
    type: Type.Literal('gssapi'),
}), XScalarSecretServiceNameSchema, ClearedSecretFieldsSchema);
export const SecretsAuthUnionSchema = Type.Union([
    SecretsApiKeySchema,
    SecretsHttpSchema,
    OAuthSchema,
    OpenIDConnectSchema,
    SecretsSaslSchema,
    SecretsX509Schema,
    SecretsEncryptionSchema,
    SecretsGssapiSchema,
]);
export const SecretsAuthSchema = Type.Record(Type.String(), SecretsAuthUnionSchema);
const SelectedSecuritySchema = Type.Object({
    selectedIndex: Type.Number(),
    selectedSchemes: Type.Array(SecurityRequirementObjectSchema),
});
export const AuthSchema = Type.Object({
    secrets: SecretsAuthSchema,
    selected: Type.Object({
        document: Type.Optional(SelectedSecuritySchema),
        path: Type.Optional(Type.Record(Type.String(), Type.Record(Type.String(), Type.Optional(SelectedSecuritySchema)))),
    }),
});
export const DocumentAuthSchema = Type.Record(Type.String(), AuthSchema);
